<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import ChannelHostingAPI from 'dashboard/api/channelHosting';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const router = useRouter();

const STEP = {
  INTRO: 'intro',
  PROXY: 'proxy',
  PAIR: 'pair',
};

const step = ref(STEP.INTRO);
const loading = ref(false);
const checkingProxy = ref(false);
const proxyVerified = ref(false);
const egressIp = ref('');
const proxyLatencyMs = ref(null);
const quota = ref({ used: 0, limit: 0, remaining: 0, can_create: false });
const displayName = ref('');
const proxy = ref({
  protocol: 'socks5',
  host: '',
  port: '',
  username: '',
  password: '',
});
const session = ref(null);
let pollTimer = null;

const remainingLabel = computed(() => {
  const q = quota.value;
  return `${q.used || 0} / ${q.limit || 0}`;
});

const qrSrc = computed(() => {
  const qr = session.value?.qr;
  if (!qr) return '';
  if (typeof qr === 'string' && qr.startsWith('data:')) return qr;
  if (typeof qr === 'string' && qr.startsWith('http')) return qr;
  if (typeof qr === 'string') return `data:image/png;base64,${qr.replace(/^data:image\/\w+;base64,/, '')}`;
  return '';
});

const loadQuota = async () => {
  try {
    const { data } = await ChannelHostingAPI.getQuota();
    quota.value = data;
  } catch (e) {
    // Debug: quota fetch failed — surface soft error for admins.
    // eslint-disable-next-line no-console
    console.debug('[channel_hosting] quota fetch failed', e);
    useAlert(t('CHANNEL_HOSTING.ERRORS.QUOTA_LOAD'));
  }
};

const goProxy = () => {
  if (!quota.value.can_create) {
    useAlert(t('CHANNEL_HOSTING.ERRORS.QUOTA_EXCEEDED'));
    return;
  }
  step.value = STEP.PROXY;
};

const resetProxyCheck = () => {
  proxyVerified.value = false;
  egressIp.value = '';
  proxyLatencyMs.value = null;
};

const checkProxy = async () => {
  if (!proxy.value.host || !proxy.value.port) {
    useAlert(t('CHANNEL_HOSTING.ERRORS.PROXY_REQUIRED'));
    return;
  }
  checkingProxy.value = true;
  resetProxyCheck();
  try {
    const { data } = await ChannelHostingAPI.checkProxy({
      protocol: proxy.value.protocol,
      host: proxy.value.host.trim(),
      port: Number(proxy.value.port),
      username: proxy.value.username,
      password: proxy.value.password,
    });
    if (data?.ok && data?.egress_ip) {
      proxyVerified.value = true;
      egressIp.value = data.egress_ip;
      proxyLatencyMs.value = data.latency_ms;
      useAlert(t('CHANNEL_HOSTING.PROXY_OK'));
    } else {
      useAlert(data?.message || t('CHANNEL_HOSTING.ERRORS.PROXY_CHECK'));
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.debug('[channel_hosting] proxy check failed', e);
    useAlert(e?.response?.data?.message || t('CHANNEL_HOSTING.ERRORS.PROXY_CHECK'));
  } finally {
    checkingProxy.value = false;
  }
};

const createSession = async () => {
  if (!proxy.value.host || !proxy.value.port) {
    useAlert(t('CHANNEL_HOSTING.ERRORS.PROXY_REQUIRED'));
    return;
  }
  if (!proxyVerified.value) {
    useAlert(t('CHANNEL_HOSTING.PROXY_CHECK_REQUIRED'));
    return;
  }
  loading.value = true;
  try {
    const { data } = await ChannelHostingAPI.createSession({
      display_name: displayName.value || undefined,
      proxy: {
        protocol: proxy.value.protocol,
        host: proxy.value.host.trim(),
        port: Number(proxy.value.port),
        username: proxy.value.username,
        password: proxy.value.password,
      },
    });
    session.value = data;
    step.value = STEP.PAIR;
    startPolling();
  } catch (e) {
    const code = e?.response?.data?.error;
    if (code === 'quota_exceeded') {
      useAlert(t('CHANNEL_HOSTING.ERRORS.QUOTA_EXCEEDED'));
    } else if (code === 'gateway_not_configured') {
      useAlert(t('CHANNEL_HOSTING.ERRORS.GATEWAY'));
    } else {
      useAlert(e?.response?.data?.message || t('CHANNEL_HOSTING.ERRORS.CREATE'));
    }
  } finally {
    loading.value = false;
  }
};

const refreshSession = async () => {
  if (!session.value?.id) return;
  try {
    const { data } = await ChannelHostingAPI.refresh(session.value.id);
    session.value = data;
    if (data.status === 'connected') {
      stopPolling();
      useAlert(t('CHANNEL_HOSTING.CONNECTED'));
      if (data.inbox_id) {
        router.replace({
          name: 'settings_inbox_finish',
          params: { inbox_id: data.inbox_id },
        });
      } else {
        router.replace({ name: 'settings_inbox_list' });
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.debug('[channel_hosting] refresh failed', e);
  }
};

const startPolling = () => {
  stopPolling();
  pollTimer = setInterval(refreshSession, 4000);
};

const stopPolling = () => {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
};

onMounted(loadQuota);
onBeforeUnmount(stopPolling);
watch(step, s => {
  if (s !== STEP.PAIR) stopPolling();
});
watch(
  proxy,
  () => {
    resetProxyCheck();
  },
  { deep: true }
);
</script>

<template>
  <div class="flex flex-col gap-6 max-w-xl">
    <div>
      <h1 class="text-lg font-medium text-n-slate-12">
        {{ t('CHANNEL_HOSTING.TITLE') }}
      </h1>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ t('CHANNEL_HOSTING.SUBTITLE') }}
      </p>
    </div>

    <div
      class="rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3 text-sm text-n-slate-12 flex items-center justify-between"
    >
      <span>{{ t('CHANNEL_HOSTING.QUOTA_LABEL') }}</span>
      <span class="font-medium tabular-nums">{{ remainingLabel }}</span>
    </div>

    <div v-if="step === STEP.INTRO" class="flex flex-col gap-4">
      <ul class="text-sm text-n-slate-11 list-disc pl-5 space-y-2">
        <li>{{ t('CHANNEL_HOSTING.INTRO_1') }}</li>
        <li>{{ t('CHANNEL_HOSTING.INTRO_2') }}</li>
        <li>{{ t('CHANNEL_HOSTING.INTRO_3') }}</li>
      </ul>
      <label class="flex flex-col gap-1 text-sm">
        <span class="text-n-slate-12">{{ t('CHANNEL_HOSTING.DISPLAY_NAME') }}</span>
        <input
          v-model="displayName"
          type="text"
          class="rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-n-slate-12"
          :placeholder="t('CHANNEL_HOSTING.DISPLAY_NAME_PH')"
        />
      </label>
      <Button
        :label="t('CHANNEL_HOSTING.CONTINUE')"
        :disabled="!quota.can_create"
        @click="goProxy"
      />
      <p v-if="!quota.can_create" class="text-sm text-n-ruby-11">
        {{ t('CHANNEL_HOSTING.ERRORS.QUOTA_EXCEEDED') }}
      </p>
    </div>

    <div v-else-if="step === STEP.PROXY" class="flex flex-col gap-4">
      <p class="text-sm text-n-slate-11">{{ t('CHANNEL_HOSTING.PROXY_HINT') }}</p>
      <div class="grid grid-cols-2 gap-3">
        <label class="flex flex-col gap-1 text-sm col-span-2 sm:col-span-1">
          <span>{{ t('CHANNEL_HOSTING.PROXY_PROTOCOL') }}</span>
          <select
            v-model="proxy.protocol"
            class="rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2"
          >
            <option value="socks5">SOCKS5</option>
            <option value="http">HTTP</option>
            <option value="https">HTTPS</option>
          </select>
        </label>
        <label class="flex flex-col gap-1 text-sm col-span-2 sm:col-span-1">
          <span>{{ t('CHANNEL_HOSTING.PROXY_PORT') }}</span>
          <input
            v-model="proxy.port"
            type="number"
            class="rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm col-span-2">
          <span>{{ t('CHANNEL_HOSTING.PROXY_HOST') }}</span>
          <input
            v-model="proxy.host"
            type="text"
            class="rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2"
            autocomplete="off"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm">
          <span>{{ t('CHANNEL_HOSTING.PROXY_USER') }}</span>
          <input
            v-model="proxy.username"
            type="text"
            class="rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2"
            autocomplete="off"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm">
          <span>{{ t('CHANNEL_HOSTING.PROXY_PASS') }}</span>
          <input
            v-model="proxy.password"
            type="password"
            class="rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2"
            autocomplete="new-password"
          />
        </label>
      </div>

      <div
        v-if="proxyVerified && egressIp"
        class="rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3 text-sm text-n-slate-12 flex flex-col gap-1"
      >
        <div class="flex justify-between gap-3">
          <span class="text-n-slate-11">{{ t('CHANNEL_HOSTING.PROXY_OK') }}</span>
          <span v-if="proxyLatencyMs != null" class="tabular-nums text-n-slate-11"
            >{{ proxyLatencyMs }} ms</span
          >
        </div>
        <div class="flex justify-between gap-3">
          <span class="text-n-slate-11">{{ t('CHANNEL_HOSTING.PROXY_EGRESS_IP') }}</span>
          <span class="font-medium tabular-nums">{{ egressIp }}</span>
        </div>
      </div>

      <div class="flex flex-wrap gap-3">
        <Button
          :label="t('CHANNEL_HOSTING.BACK')"
          slate
          @click="step = STEP.INTRO"
        />
        <Button
          :label="
            checkingProxy
              ? t('CHANNEL_HOSTING.CHECKING_PROXY')
              : t('CHANNEL_HOSTING.CHECK_PROXY')
          "
          slate
          :is-loading="checkingProxy"
          @click="checkProxy"
        />
        <Button
          :label="t('CHANNEL_HOSTING.SHOW_QR')"
          :is-loading="loading"
          :disabled="!proxyVerified"
          @click="createSession"
        />
      </div>
    </div>

    <div v-else class="flex flex-col gap-4 items-start">
      <p class="text-sm text-n-slate-11">{{ t('CHANNEL_HOSTING.PAIR_HINT') }}</p>
      <div
        class="rounded-2xl border border-n-weak bg-white p-4 min-h-48 min-w-48 flex items-center justify-center"
      >
        <img
          v-if="qrSrc"
          :src="qrSrc"
          alt="QR"
          class="w-48 h-48 object-contain"
        />
        <span v-else class="text-sm text-n-slate-11">{{
          t('CHANNEL_HOSTING.WAITING_QR')
        }}</span>
      </div>
      <p class="text-sm text-n-slate-12">
        {{ t('CHANNEL_HOSTING.STATUS') }}:
        <span class="font-medium">{{ session?.status }}</span>
      </p>
      <Button :label="t('CHANNEL_HOSTING.REFRESH')" slate @click="refreshSession" />
    </div>
  </div>
</template>
